import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import classnames from 'classnames'
import map from 'lodash/map'
import filter from 'lodash/filter'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import SiteCheckbox from '@SiteComponents/SiteCheckbox'
import Arrow from '@Images/search_dropdown_arrow.svg'

import './MultiselectionDropdown.scss'

const DEFAULT_PORTAL_CONTAINER_ID = 'site-container'
const DROPDOWN_GAP_Y = 6 // px

interface MultiselectionDropdownProps {
  readonly availableOptions: string[]
  readonly availableOptionDescriptions?: string[]
  readonly selectedOptionIndices: number[]
  readonly entityName: string
  readonly icon?: JSX.Element
  readonly classNameForClickOutsideListener: string
  readonly classNameOfContainerToMakeUnscrollable?: string
  readonly paddingToCompensateScrollbarWhenUnscrollable?: number
  readonly idNameOfParentContainer: string
  readonly disabled?: boolean
  readonly isForExportation?: boolean
  readonly showAllCounter?: boolean
  readonly portalContainerId?: string
  setSelectedOptionIndices(newSelectedOptionIndices: number[]): void
}

const MultiselectionDropdown: React.FC<MultiselectionDropdownProps> = props => {
  const {
    availableOptions,
    selectedOptionIndices,
    classNameForClickOutsideListener,
    entityName,
    icon,
    availableOptionDescriptions,
    classNameOfContainerToMakeUnscrollable,
    paddingToCompensateScrollbarWhenUnscrollable,
    disabled,
    isForExportation,
    showAllCounter,
    idNameOfParentContainer,
    portalContainerId = DEFAULT_PORTAL_CONTAINER_ID,
  } = props

  const [showDropdown, setShowDropdown] = useState(false)
  const numberOfSelectedOptions = selectedOptionIndices.length
  const allOptionsSelected = selectedOptionIndices.length === availableOptions.length
  const allOptionsCheckboxValue = allOptionsSelected ? 'checked' : 'unchecked'
  const hasDescriptions = !!availableOptionDescriptions
  const disabledAndNoLanguagesSelected = availableOptions.length === 0 && disabled

  let selectedOptionsSummary = `No ${entityName} selected`
  if (allOptionsSelected && !disabledAndNoLanguagesSelected) {
    selectedOptionsSummary = `All ${entityName}s ${showAllCounter && `(${availableOptions.length})`}`
  } else if (numberOfSelectedOptions > 0){
    selectedOptionsSummary = numberOfSelectedOptions === 1 ? `1 ${entityName} selected` : `${numberOfSelectedOptions} ${entityName}s selected`
  }

  const toggleDropdown = () => {
    openOrCloseDropdown(!showDropdown)
  }

  const openOrCloseDropdown = (newOpenDropdown: boolean)  => {
    if (disabled) { return }

    setShowDropdown(newOpenDropdown)

    if (classNameOfContainerToMakeUnscrollable) {
      const container = document.getElementsByClassName(classNameOfContainerToMakeUnscrollable)
      if (container && container.length > 0) {
        const containerElement = container[0] as HTMLElement
        containerElement.style.overflowY = newOpenDropdown ? 'hidden' : 'scroll'

        if (typeof(paddingToCompensateScrollbarWhenUnscrollable) === 'number') {
          containerElement.style.paddingRight = newOpenDropdown ? paddingToCompensateScrollbarWhenUnscrollable + 'px' : '0px'
        }
      }
    }
  }

  useClickOutsideListener(classNameForClickOutsideListener, openOrCloseDropdown, showDropdown)

  const handleAllOptionsSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelectedOptionIndices = e.target.checked ? map(availableOptions, (_, index) => index) : []
    props.setSelectedOptionIndices(newSelectedOptionIndices)
  }

  const optionsList = map(availableOptions, (availableOption, index) => {
    const selected = selectedOptionIndices.includes(index)
    const checkboxValue = selected ? 'checked' : 'unchecked'

    const handleOptionSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSelectedOptionIndices = e.target.checked ? selectedOptionIndices.concat(index) : filter(selectedOptionIndices, selectedOptionIndex => selectedOptionIndex !== index)
      props.setSelectedOptionIndices(newSelectedOptionIndices)
    }

    return (
      <li
        key={index+1}
        className={classnames(
          'MultiselectionDropdown__item',
          { 'MultiselectionDropdown__item-description': hasDescriptions },
        )}
      >
        <SiteCheckbox
          key={`${index}-${checkboxValue}`}
          id={index+1}
          checkboxValue={checkboxValue as 'checked' | 'unchecked' | 'indeterminate'}
          onChange={handleOptionSelection}
          entityNameForIds={entityName}
        />
        <label
          className="MultiselectionDropdown__item-label"
          htmlFor={`${entityName}_${index+1}`}
        >
          <div className="MultiselectionDropdown__item-label-main">
            {availableOption}
          </div>
          {hasDescriptions && (
            <div className="MultiselectionDropdown__item-label-description">
              {availableOptionDescriptions[index]}
            </div>
          )}
        </label>
      </li>
    )
  })

  const renderAllOptions = () => {
    return (
      <div className="MultiselectionDropdown__item MultiselectionDropdown__item-all">
        <SiteCheckbox
          key={`all-${allOptionsCheckboxValue}`}
          id={0}
          checkboxValue={allOptionsCheckboxValue as 'checked' | 'unchecked' | 'indeterminate'}
          onChange={handleAllOptionsSelection}
          entityNameForIds={entityName}
        />
        <label
          className="MultiselectionDropdown__item-label"
          htmlFor={`${entityName}_0`}
        >
          <div className="MultiselectionDropdown__item-label-main">
            {`All ${entityName}s ${showAllCounter && `(${availableOptions.length})`}`}
          </div>
        </label>
      </div>
    )
  }

  const dropdownParentElement = document.getElementById(idNameOfParentContainer)
  const shouldRenderInBody = portalContainerId === 'body'
  const portalContainer = shouldRenderInBody ? document.body : document.getElementById(portalContainerId) || document.body
  let dropdownX = 0
  let dropdownY = 0

  if (dropdownParentElement) {
    const dropdownParentElementBounds = dropdownParentElement.getBoundingClientRect()
    const shouldPositionRelativeToPortal = portalContainerId !== DEFAULT_PORTAL_CONTAINER_ID

    if (shouldRenderInBody) {
      dropdownX = dropdownParentElementBounds.left
      dropdownY = dropdownParentElementBounds.bottom + DROPDOWN_GAP_Y
    } else if (shouldPositionRelativeToPortal) {
      const portalContainerBounds = portalContainer.getBoundingClientRect()
      dropdownX = dropdownParentElementBounds.left - portalContainerBounds.left
      dropdownY = dropdownParentElementBounds.bottom - portalContainerBounds.top + DROPDOWN_GAP_Y
    } else {
      dropdownX = dropdownParentElementBounds.left
      dropdownY = dropdownParentElementBounds.bottom + DROPDOWN_GAP_Y
    }
  }

  const dropdownPositioningStyle: React.CSSProperties = {
    position: shouldRenderInBody ? 'fixed' : undefined,
    top: `${dropdownY}px`,
    left: `${dropdownX}px`,
  }

  const renderDropdown = () => {
    return ReactDOM.createPortal(
      (
        <div
          className={classnames(
            'MultiselectionDropdown__dropdown',
            `${classNameForClickOutsideListener}`,
            { 'MultiselectionDropdown__dropdown-export': isForExportation },
          )}
          style={dropdownPositioningStyle}
        >
          {renderAllOptions()}
          <ul className="MultiselectionDropdown__items">
            {optionsList}
          </ul>
        </div>
      ),
      portalContainer
    )
  }

  return (
    <div
      className={classnames(
        'MultiselectionDropdown',
        `${classNameForClickOutsideListener}`,
        { 'MultiselectionDropdown__export': isForExportation },
      )}
    >
      <div
        className={classnames(
          'MultiselectionDropdown__selected',
          { 'MultiselectionDropdown__selected-disabled': disabled },
        )}
        onClick={toggleDropdown}
      >
        <div className="MultiselectionDropdown__selected-left">
          {icon}
          {selectedOptionsSummary}
        </div>
        {!disabled && (
          <Arrow
            className={classnames(
              'MultiselectionDropdown__selected-arrow',
              { 'MultiselectionDropdown__selected-arrow-rotated': showDropdown },
            )}
          />
        )}
      </div>
      {showDropdown && renderDropdown()}
    </div>
  )
}

export default MultiselectionDropdown
